import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AdminService,
  EmployeeSalaryMassUpdateRowInput,
  EmployeeSalaryMassUpdateResult,
} from '../../../core/services/admin.service';
import { BaseModalComponent } from './base-modal.component';
import {
  SpreadsheetGridCellContext,
  SpreadsheetGridColumnDefinition,
  SpreadsheetGridEdgeCellContext,
  SpreadsheetGridEdgeColumnDefinition,
  SpreadsheetGridRow,
  SpreadsheetGridComponent,
} from '../spreadsheet-grid/spreadsheet-grid.component';

const COLUMN_DEFINITIONS: SpreadsheetGridColumnDefinition[] = [
  {
    key: 'employee_id',
    headerPath: ['Medarbetare', 'employee_id'],
    band: 'employee',
    kind: 'id',
    width: '0.75fr',
    inputMode: 'numeric',
  },
  {
    key: 'name',
    headerPath: ['Medarbetare', 'namn'],
    band: 'employee',
    kind: 'text',
    width: '2.2fr',
    inputMode: 'text',
  },
  {
    key: 'old_salary',
    headerPath: ['Gammal', 'lön'],
    band: 'old',
    kind: 'number',
    width: '0.9fr',
    inputMode: 'numeric',
  },
  {
    key: 'old_bonus',
    headerPath: ['Gammal', 'bonus'],
    band: 'old',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'old_overtime',
    headerPath: ['Gammal', 'övertid'],
    band: 'old',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'old_cbonus',
    headerPath: ['Gammal', 'c-bonus'],
    band: 'old',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'old_responsability',
    headerPath: ['Gammal', 'ansvar'],
    band: 'old',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'old_time',
    headerPath: ['Gammal', 'tid'],
    band: 'old',
    kind: 'number',
    width: '0.65fr',
    inputMode: 'decimal',
  },
  {
    key: 'new_startdate',
    headerPath: ['Ny', 'datum'],
    band: 'new',
    kind: 'date',
    width: '1fr',
    inputMode: 'text',
    placeholder: 'yyyy-mm-dd',
  },
  {
    key: 'new_salary',
    headerPath: ['Ny', 'lön'],
    band: 'new',
    kind: 'number',
    width: '0.9fr',
    inputMode: 'numeric',
  },
  {
    key: 'new_bonus',
    headerPath: ['Ny', 'bonus'],
    band: 'new',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'new_overtime',
    headerPath: ['Ny', 'övertid'],
    band: 'new',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'new_cbonus',
    headerPath: ['Ny', 'c-bonus'],
    band: 'new',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'new_responsability',
    headerPath: ['Ny', 'ansvar'],
    band: 'new',
    kind: 'number',
    width: '0.7fr',
    inputMode: 'numeric',
  },
  {
    key: 'new_time',
    headerPath: ['Ny', 'tid'],
    band: 'new',
    kind: 'number',
    width: '0.65fr',
    inputMode: 'decimal',
  },
];

@Component({
  selector: 'app-employee-salary-mass-update-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SpreadsheetGridComponent],
  template: require('./employee-salary-mass-update-modal.component.html').default,
  styles: [
    require('!!raw-loader!sass-loader!./base-modal.component.scss').default,
    require('!!raw-loader!sass-loader!./employee-salary-mass-update-modal.component.scss').default,
  ],
  animations: [BaseModalComponent.modalAnimations[0]],
})
export class EmployeeSalaryMassUpdateModalComponent extends BaseModalComponent {
  readonly columns = COLUMN_DEFINITIONS;
  readonly leftEdgeColumn: SpreadsheetGridEdgeColumnDefinition = {
    key: 'row-index',
    headerPath: ['#'],
    width: '0.45fr',
    band: 'edge',
  };
  readonly rightEdgeColumn: SpreadsheetGridEdgeColumnDefinition = {
    key: 'row-action',
    headerPath: ['Rad'],
    width: '0.8fr',
    band: 'edge',
  };
  parsingErrors: string[] = [];
  parsedRows: EmployeeSalaryMassUpdateRowInput[] = [];
  private nextRowId = 1;
  gridRows: SpreadsheetGridRow[] = [this.createEmptyGridRow()];
  validationResult: EmployeeSalaryMassUpdateResult | null = null;
  loading = false;
  saveInProgress = false;

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  protected override focusFirstElement(): void {
    const textarea = document.getElementById('salary-mass-update-source');
    if (textarea) {
      textarea.focus();
    }
  }

  get columnHelp(): string {
    return this.columns.map((column) => column.headerPath[column.headerPath.length - 1]).join('\t');
  }

  canPreview(): boolean {
    return !this.loading && this.hasAnyDataRow();
  }

  canSave(): boolean {
    return !this.loading && !this.saveInProgress && this.validationResult?.ok === true;
  }

  addRow(): void {
    this.gridRows = [...this.gridRows, this.createEmptyGridRow()];
    this.onGridChange();
  }

  removeRow(index: number): void {
    this.gridRows.splice(index, 1);
    if (this.gridRows.length === 0) {
      this.gridRows = [this.createEmptyGridRow()];
    }
    this.onGridChange();
  }

  onCellValueChange(): void {
    this.validationResult = null;
    this.parsingErrors = [];
  }

  onCellBlur(rowId: number): void {
    const rowIndex = this.gridRows.findIndex((row) => row.id === rowId);
    if (rowIndex === -1 || rowIndex !== this.gridRows.length - 1) {
      return;
    }

    const row = this.gridRows[rowIndex];
    if (!row.cells.some((cell) => cell.trim() !== '')) {
      return;
    }

    this.gridRows.push(this.createEmptyGridRow());
    this.changeDetectorRef.detectChanges();
  }

  onGridChange(): void {
    this.ensureTrailingEmptyRow();
    this.validationResult = null;
    this.parsingErrors = [];
  }

  onCellPaste(event: ClipboardEvent, rowIndex: number, columnIndex: number): void {
    const clipboardText = event.clipboardData?.getData('text/plain') || '';
    if (!clipboardText.includes('\t') && !clipboardText.includes('\n')) {
      return;
    }

    event.preventDefault();

    const pastedRows = clipboardText
      .replace(/\r/g, '')
      .split('\n')
      .filter((line) => line !== '')
      .map((line) => line.split('\t'));

    while (this.gridRows.length < rowIndex + pastedRows.length) {
      this.gridRows.push(this.createEmptyGridRow());
    }

    pastedRows.forEach((cells, pasteRowIndex) => {
      const targetRow = this.gridRows[rowIndex + pasteRowIndex].cells;
      cells.forEach((cell, pasteColumnIndex) => {
        const targetColumnIndex = columnIndex + pasteColumnIndex;
        if (targetColumnIndex < this.columns.length) {
          targetRow[targetColumnIndex] = cell.trim();
        }
      });
    });

    this.onGridChange();
  }

  preview(): void {
    this.validationResult = null;
    const parseResult = this.parseGridRows();
    this.parsingErrors = parseResult.errors;
    this.parsedRows = parseResult.rows;

    if (this.parsingErrors.length > 0 || this.parsedRows.length === 0) {
      return;
    }

    this.loading = true;
    this.adminService.previewEmployeeSalaryMassUpdate(this.parsedRows).subscribe({
      next: (result) => {
        setTimeout(() => {
          this.validationResult = result;
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        });
      },
      error: () => {
        setTimeout(() => {
          this.parsingErrors = ['Det gick inte att validera massuppdateringen.'];
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        });
      },
    });
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }

    this.saveInProgress = true;
    this.adminService.applyEmployeeSalaryMassUpdate(this.parsedRows).subscribe({
      next: (result) => {
        setTimeout(() => {
          this.saveInProgress = false;
          if (!result.ok) {
            this.validationResult = result;
            this.changeDetectorRef.detectChanges();
            return;
          }
          this.changeDetectorRef.detectChanges();
          this.confirm(result);
        });
      },
      error: (err) => {
        setTimeout(() => {
          this.saveInProgress = false;
          const backendRows = err?.error?.rows;
          if (backendRows) {
            this.validationResult = {
              ok: false,
              rows: backendRows,
            };
          } else {
            this.parsingErrors = ['Det gick inte att spara massuppdateringen.'];
          }
          this.changeDetectorRef.detectChanges();
        });
      },
    });
  }

  trackByRowId(_index: number, row: SpreadsheetGridRow): number | string {
    return row.id;
  }

  cellInputClass(column: SpreadsheetGridColumnDefinition): string[] {
    return ['form-control', 'input-sm', 'spreadsheet-grid-input', `spreadsheet-grid-input-${column.kind || 'text'}`];
  }

  private parseGridRows(): { rows: EmployeeSalaryMassUpdateRowInput[]; errors: string[] } {
    const sourceRows = this.gridRows
      .map((row) => row.cells.map((cell) => cell.trim()))
      .filter((row) => row.some((cell) => cell !== ''));

    if (sourceRows.length === 0) {
      return { rows: [], errors: ['Fyll i minst en rad i matrisen.'] };
    }

    const rows: EmployeeSalaryMassUpdateRowInput[] = [];
    const errors: string[] = [];
    const firstCells = sourceRows[0];
    const hasHeader = this.looksLikeHeaderRow(firstCells);
    const dataRows = hasHeader ? sourceRows.slice(1) : sourceRows;

    dataRows.forEach((columns, index) => {
      if (columns.length !== this.columns.length) {
        errors.push(`Rad ${index + 1}: förväntade ${this.columns.length} kolumner men fick ${columns.length}.`);
        return;
      }

      try {
        rows.push({
          employee_id: this.parseIntValue(columns[0]),
          name: columns[1].trim(),
          old_salary: this.parseIntValue(columns[2]),
          old_bonus: this.parseIntValue(columns[3], 0),
          old_overtime: this.parseIntValue(columns[4], 0),
          old_cbonus: this.parseIntValue(columns[5], 0),
          old_responsability: this.parseIntValue(columns[6], 0),
          old_time: this.parseFloatValue(columns[7], 1),
          new_startdate: columns[8].trim(),
          new_salary: this.parseIntValue(columns[9]),
          new_bonus: this.parseIntValue(columns[10], 0),
          new_overtime: this.parseIntValue(columns[11], 0),
          new_cbonus: this.parseIntValue(columns[12], 0),
          new_responsability: this.parseIntValue(columns[13], 0),
          new_time: this.parseFloatValue(columns[14], 1),
        });
      } catch (err) {
        errors.push(`Rad ${index + 1}: ${(err as Error).message}`);
      }
    });

    return { rows, errors };
  }

  private looksLikeHeaderRow(row: string[]): boolean {
    const normalized = row.map((cell) => cell.toLowerCase().replace(/\s+/g, '_'));
    return normalized[0] === 'employee_id' || normalized[1] === 'namn';
  }

  private hasAnyDataRow(): boolean {
    return this.gridRows.some((row) => row.cells.some((cell) => cell.trim() !== ''));
  }

  private ensureTrailingEmptyRow(): void {
    if (this.gridRows.length === 0) {
      this.gridRows = [this.createEmptyGridRow()];
      return;
    }

    const lastRow = this.gridRows[this.gridRows.length - 1];
    if (lastRow.cells.some((cell) => cell.trim() !== '')) {
      this.gridRows.push(this.createEmptyGridRow());
    }
  }

  private createEmptyGridRow(): SpreadsheetGridRow {
    return {
      id: this.nextRowId++,
      cells: Array.from({ length: this.columns.length }, () => ''),
    };
  }

  private parseIntValue(value: string, fallback?: number): number {
    const normalized = value.trim();
    if (normalized === '' && fallback !== undefined) {
      return fallback;
    }
    if (!/^-?\d+$/.test(normalized)) {
      throw new Error(`"${value}" måste vara ett heltal.`);
    }
    return Number.parseInt(normalized, 10);
  }

  private parseFloatValue(value: string, fallback?: number): number {
    const normalized = value.trim().replace(',', '.');
    if (normalized === '' && fallback !== undefined) {
      return fallback;
    }
    if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) {
      throw new Error(`"${value}" måste vara ett tal.`);
    }
    return Number.parseFloat(normalized);
  }
}
